import React, { useState, useEffect } from "react";
import { DataGrid, gridClasses } from "@mui/x-data-grid";
import {
  IconButton,
  Menu,
  MenuItem,
  Button,
  TextField,
  Tooltip,
  Modal,
} from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { BsThreeDotsVertical } from "react-icons/bs";
import { BiImport } from "react-icons/bi";
import { MdEdit, MdSave } from "react-icons/md";
import { RxCross1 } from "react-icons/rx";
import { TbFilterEdit } from "react-icons/tb";
import { MdArchive } from "react-icons/md";
import { TbAlertHexagonFilled } from "react-icons/tb";
import { HttpService } from "../../service/HttpService";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import RoomLogImportModal from "./RoomLogImportModal";
import "./RoomLogMasterTable.css";

const RoomLogMasterTable = ({
  loading: externalLoading = false,
  roomId: externalRoomId,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedRowId, setSelectedRowId] = useState(null);
  const [showImportModal, setShowImportModal] = useState(false);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [epicenterRows, setEpicenterRows] = useState([]);
  const [epicenterLoading, setEpicenterLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [epicenterSearch, setEpicenterSearch] = useState("");
  const [editingRowId, setEditingRowId] = useState(null);
  const [editedData, setEditedData] = useState({});
  const [planted, setPlanted] = useState("");
  const [harvest, setHarvest] = useState("");
  const [editingNoteRowId, setEditingNoteRowId] = useState(null);
  const [editedNote, setEditedNote] = useState("");
  const [noteAnchorEl, setNoteAnchorEl] = useState(null);
  const [selectedNoteRowId, setSelectedNoteRowId] = useState(null);
  const [showArchiveDialog, setShowArchiveDialog] = useState(false);
  const [debounceTimers, setDebounceTimers] = useState({});
  const [hasNonDefault, setHasNonDefault] = useState(false);

  const httpService = new HttpService();

  const effectiveRoomId = externalRoomId || roomId;

  useEffect(() => {
    return () => {
      Object.values(debounceTimers).forEach((timer) => clearTimeout(timer));
    };
  }, [debounceTimers]);

  const handleMenuClick = (event, rowId) => {
    setAnchorEl(event.currentTarget);
    setSelectedRowId(rowId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleImportClick = (rowData) => {
    setSelectedRowId(rowData.growth_phase_id);
    setShowImportModal(true);
    handleMenuClose();
  };

  const handleEditClick = (rowData) => {
    if (rowData.id < 0) {
      toast.error("Cannot edit this row");
      return;
    }
    setEditingRowId(rowData.id);
    setEditedData(rowData);
    handleMenuClose();
  };

  const handleFieldChange = async (field, value) => {
    const updatedData = { ...editedData, [field]: value };
    setEditedData(updatedData);

    try {
      const payload = {
        day_temp_target: parseFloat(updatedData.day_temp_target) || 0,
        night_temp_target: parseFloat(updatedData.night_temp_target) || 0,
        day_humidity_target: parseFloat(updatedData.day_humidity_target) || 0,
        night_humidity_target:
          parseFloat(updatedData.night_humidity_target) || 0,
        ph_target: parseFloat(updatedData.ph_target) || 0,
        tds_target: parseFloat(updatedData.tds_target) || 0,
        water_temp_target: parseFloat(updatedData.water_temp_target) || 0,
        co2_target: parseFloat(updatedData.co2_target) || 0,
        avg_day_temp: parseFloat(updatedData.avg_day_temp) || 0,
        avg_night_temp: parseFloat(updatedData.avg_night_temp) || 0,
        avg_day_humidity: parseFloat(updatedData.avg_day_humidity) || 0,
        avg_night_humidity: parseFloat(updatedData.avg_night_humidity) || 0,
        epi01_ph_avg: parseFloat(updatedData.epi01_ph_avg) || 0,
        epi02_ph_avg: parseFloat(updatedData.epi02_ph_avg) || 0,
        epi03_ph_avg: parseFloat(updatedData.epi03_ph_avg) || 0,
        epi01_tds_avg: parseFloat(updatedData.epi01_tds_avg) || 0,
        epi02_tds_avg: parseFloat(updatedData.epi02_tds_avg) || 0,
        epi03_tds_avg: parseFloat(updatedData.epi03_tds_avg) || 0,
        epi01_water_temp_avg: parseFloat(updatedData.epi01_water_temp_avg) || 0,
        epi02_water_temp_avg: parseFloat(updatedData.epi02_water_temp_avg) || 0,
        epi03_water_temp_avg: parseFloat(updatedData.epi03_water_temp_avg) || 0,
        avg_light_level: parseFloat(updatedData.avg_light_level) || 0,
        avg_co2: parseFloat(updatedData.avg_co2) || 0,
      };

      await httpService.put(`room-logs/update/${editingRowId}`, payload);
      toast.success("Updated successfully");
      fetchRoomLogs();
    } catch (error) {
      toast.error("Update failed. Please try again.");
    }
  };

  const handleImport = async (file) => {
    try {
      if (!selectedRowId || !selectedRowId) {
        toast.error("Growth phase not found");
        return;
      }
      const formData = new FormData();
      formData.append("file", file);
      formData.append("growth_phase_id", selectedRowId);
      const response = await httpService.post(
        `room-logs/file/${effectiveRoomId}`,
        formData,
      );
      if (response?.success) {
        toast.success("File imported successfully");
        fetchRoomLogs();
      } else {
        toast.error(response?.message || "Import failed");
      }
    } catch (error) {
      toast.error("Import failed. Please try again.");
    }
  };

  const handleNoteChange = (value, rowId, growth_phase_id) => {
    if (debounceTimers[rowId]) {
      clearTimeout(debounceTimers[rowId]);
    }

    const timer = setTimeout(async () => {
      try {
        const row = rows.find((r) => r.id === rowId);
        if (!row) return;

        const payload = {
          // day_temp_target: parseFloat(row.day_temp_target) || 0,
          // night_temp_target: parseFloat(row.night_temp_target) || 0,
          // day_humidity_target: parseFloat(row.day_humidity_target) || 0,
          // night_humidity_target: parseFloat(row.night_humidity_target) || 0,
          // ph_target: parseFloat(row.ph_target) || 0,
          // tds_target: parseFloat(row.tds_target) || 0,
          // water_temp_target: parseFloat(row.water_temp_target) || 0,
          // co2_target: parseFloat(row.co2_target) || 0,
          // avg_day_temp: parseFloat(row.avg_day_temp) || 0,
          // avg_night_temp: parseFloat(row.avg_night_temp) || 0,
          // avg_day_humidity: parseFloat(row.avg_day_humidity) || 0,
          // avg_night_humidity: parseFloat(row.avg_night_humidity) || 0,
          // epi01_ph_avg: parseFloat(row.epi01_ph_avg) || 0,
          // epi02_ph_avg: parseFloat(row.epi02_ph_avg) || 0,
          // epi03_ph_avg: parseFloat(row.epi03_ph_avg) || 0,
          // epi01_tds_avg: parseFloat(row.epi01_tds_avg) || 0,
          // epi02_tds_avg: parseFloat(row.epi02_tds_avg) || 0,
          // epi03_tds_avg: parseFloat(row.epi03_tds_avg) || 0,
          // epi01_water_temp_avg: parseFloat(row.epi01_water_temp_avg) || 0,
          // epi02_water_temp_avg: parseFloat(row.epi02_water_temp_avg) || 0,
          // epi03_water_temp_avg: parseFloat(row.epi03_water_temp_avg) || 0,
          // avg_light_level: parseFloat(row.avg_light_level) || 0,
          // avg_co2: parseFloat(row.avg_co2) || 0,
          note: value,
        };
        await httpService.post(
          `room-logs/note/${effectiveRoomId}/${growth_phase_id}`,
          payload,
        );
        fetchRoomLogs();
        toast.success("Note Saved Successfully");
      } catch {
        toast.error("Failed to save note");
      }
    }, 2000);

    setDebounceTimers((prev) => ({ ...prev, [rowId]: timer }));
  };

  const handleSaveNote = async (params) => {
    try {
      const payload = {
        day_temp_target: parseFloat(params.row.day_temp_target) || 0,
        night_temp_target: parseFloat(params.row.night_temp_target) || 0,
        day_humidity_target: parseFloat(params.row.day_humidity_target) || 0,
        night_humidity_target:
          parseFloat(params.row.night_humidity_target) || 0,
        ph_target: parseFloat(params.row.ph_target) || 0,
        tds_target: parseFloat(params.row.tds_target) || 0,
        water_temp_target: parseFloat(params.row.water_temp_target) || 0,
        co2_target: parseFloat(params.row.co2_target) || 0,
        avg_day_temp: parseFloat(params.row.avg_day_temp) || 0,
        avg_night_temp: parseFloat(params.row.avg_night_temp) || 0,
        avg_day_humidity: parseFloat(params.row.avg_day_humidity) || 0,
        avg_night_humidity: parseFloat(params.row.avg_night_humidity) || 0,
        epi01_ph_avg: parseFloat(params.row.epi01_ph_avg) || 0,
        epi02_ph_avg: parseFloat(params.row.epi02_ph_avg) || 0,
        epi03_ph_avg: parseFloat(params.row.epi03_ph_avg) || 0,
        epi01_tds_avg: parseFloat(params.row.epi01_tds_avg) || 0,
        epi02_tds_avg: parseFloat(params.row.epi02_tds_avg) || 0,
        epi03_tds_avg: parseFloat(params.row.epi03_tds_avg) || 0,
        epi01_water_temp_avg: parseFloat(params.row.epi01_water_temp_avg) || 0,
        epi02_water_temp_avg: parseFloat(params.row.epi02_water_temp_avg) || 0,
        epi03_water_temp_avg: parseFloat(params.row.epi03_water_temp_avg) || 0,
        avg_light_level: parseFloat(params.row.avg_light_level) || 0,
        avg_co2: parseFloat(params.row.avg_co2) || 0,
        note: editedNote,
      };
      await httpService.put(`room-logs/update/${params.row.id}`, payload);
      toast.success("Note updated successfully");
      setEditingNoteRowId(null);
      setEditedNote("");
      fetchRoomLogs();
    } catch {
      toast.error("Failed to update note");
    }
  };

  const handleArchive = async () => {
    if (!planted) {
      toast.error("There is no plant in the room");
      return;
    }
    setShowArchiveDialog(false);
    try {
      const payload = {
        planted_date: planted ? dayjs(planted).format("YYYY-MM-DD") : null,
        harvested_date: harvest ? dayjs(harvest).format("YYYY-MM-DD") : null,
      };
      await httpService.post(`room-logs/archive/${effectiveRoomId}`, payload);
      toast.success("Archived successfully");
      fetchRoomLogs();
    } catch (error) {
      toast.error("Archive failed. Please try again.");
    }
  };
  const getColorStyle = (color) => {
    const colors = {
      RED: "#ffebee",
      YELLOW: "#fff9c4",
      GREEN: "#e8f5e9",
    };
    return colors[color] || "transparent";
  };

  const fetchRoomLogs = async () => {
    setLoading(true);
    try {
      const res = await httpService.get(
        `room-logs/default/${effectiveRoomId}/logs`,
      );
      const list = res?.data || [];
      const mapped = Array.isArray(list)
        ? list.map((row) => ({
            id: row.id,
            growth_phase: row.growth_phase_name,
            growth_phase_id: row.growth_phase_id,
            note: row.note || "",
            day_temp_target: parseFloat(row.day_temp_target) || 0,
            avg_day_temp: parseFloat(row.average_day_temp) || 0,
            avg_day_temp_color: row.average_day_temp_color,
            night_temp_target: parseFloat(row.night_temp_target) || 0,
            avg_night_temp: parseFloat(row.average_night_temp) || 0,
            avg_night_temp_color: row.average_night_temp_color,
            day_humidity_target: parseFloat(row.day_humidity_target) || 0,
            avg_day_humidity: parseFloat(row.average_day_humidity) || 0,
            avg_day_humidity_color: row.average_day_humidity_color,
            night_humidity_target: parseFloat(row.night_humidity_target) || 0,
            avg_night_humidity: parseFloat(row.average_night_humidity) || 0,
            avg_night_humidity_color: row.average_night_humidity_color,
            ph_target: parseFloat(row.ph_target) || 0,
            epi01_ph_avg: parseFloat(row.epi_01_ph_avg) || 0,
            epi01_ph_avg_color: row.epi_01_ph_avg_color,
            epi02_ph_avg: parseFloat(row.epi_02_ph_avg) || 0,
            epi02_ph_avg_color: row.epi_02_ph_avg_color,
            epi03_ph_avg: parseFloat(row.epi_03_ph_avg) || 0,
            epi03_ph_avg_color: row.epi_03_ph_avg_color,
            tds_target: parseFloat(row.tds_target) || 0,
            epi01_tds_avg: parseFloat(row.epi_01_tds_avg) || 0,
            epi01_tds_avg_color: row.epi_01_tds_avg_color,
            epi02_tds_avg: parseFloat(row.epi_02_tds_avg) || 0,
            epi02_tds_avg_color: row.epi_02_tds_avg_color,
            epi03_tds_avg: parseFloat(row.epi_03_tds_avg) || 0,
            epi03_tds_avg_color: row.epi_03_tds_avg_color,
            water_temp_target: parseFloat(row.water_temp_target) || 0,
            epi01_water_temp_avg: parseFloat(row.epi_01_water_temp_avg) || 0,
            epi01_water_temp_avg_color: row.epi_01_water_temp_avg_color,
            epi02_water_temp_avg: parseFloat(row.epi_02_water_temp_avg) || 0,
            epi02_water_temp_avg_color: row.epi_02_water_temp_avg_color,
            epi03_water_temp_avg: parseFloat(row.epi_03_water_temp_avg) || 0,
            epi03_water_temp_avg_color: row.epi_03_water_temp_avg_color,
            avg_light_level: parseFloat(row.average_light_level_avg) || 0,
            co2_target: parseFloat(row.co2_target) || 0,
            avg_co2: parseFloat(row.average_co2_avg) || 0,
            avg_co2_color: row.average_co2_avg_color,
            last_updated: row.date_last_updated
              ? dayjs(row.date_last_updated).format("DD-MM-YYYY")
              : "",
          }))
        : [];

      setHasNonDefault(list.some((row) => row.is_default === false));
      setRows(mapped);
    } catch (e) {
      setRows([]);
      toast.error("Failed to fetch room logs");
    }
    setLoading(false);
  };

  const formatEpicenter = (value) => {
    if (!value) return "-";
    return `Epicenter ${value.replace("E", "")}`;
  };

  const fetchEpicenterCultivar = async () => {
    setEpicenterLoading(true);
    try {
      const res = await httpService.get(
        `room-logs/epicenter-cultivar/${effectiveRoomId}`,
      );

      const list = res?.data || [];
      const mapped = list.map((item, index) => ({
        id: index + 1,
        epicenter_code: item.epicenter, // E1, E2, E3
        planting_location: formatEpicenter(item.epicenter),
        cultivar: item.cultivar_name,
      }));

      setEpicenterRows(mapped);
    } catch {
      setEpicenterRows([]);
      toast.error("Failed to fetch epicenter cultivar data");
    }
    setEpicenterLoading(false);
  };

  const checkAndFetchDates = async () => {
    try {
      const [masterRes, datesRes] = await Promise.all([
        httpService.get(`room-logs/master-dates/${effectiveRoomId}`),
        httpService.get(`room-logs/dates/${effectiveRoomId}`),
      ]);

      const plantedDate = datesRes?.data?.planted_date
        ? dayjs(datesRes.data.planted_date)
        : masterRes?.data?.planted_date
          ? dayjs(masterRes.data.planted_date)
          : null;

      const harvestDate = masterRes?.data?.harvested_date
        ? dayjs(masterRes.data.harvested_date)
        : datesRes?.data?.harvested_date
          ? dayjs(datesRes.data.harvested_date)
          : null;

      setPlanted(plantedDate);
      setHarvest(harvestDate);
    } catch {
      toast.error("Failed to fetch dates");
    }
  };

  const handleDateChange = async (field, value) => {
    try {
      const payload = {
        planted_date:
          field === "planted"
            ? value
              ? dayjs(value).format("YYYY-MM-DD")
              : null
            : planted
              ? dayjs(planted).format("YYYY-MM-DD")
              : null,
        harvested_date:
          field === "harvest"
            ? value
              ? dayjs(value).format("YYYY-MM-DD")
              : null
            : harvest
              ? dayjs(harvest).format("YYYY-MM-DD")
              : null,
      };
      await httpService.post(
        `room-logs/Upsert_dates/${effectiveRoomId}`,
        payload,
      );
      if (field === "planted") setPlanted(value);
      if (field === "harvest") setHarvest(value);
      toast.success("Date updated successfully");
    } catch {
      toast.error("Failed to update date");
    }
  };

  useEffect(() => {
    if (effectiveRoomId) {
      fetchRoomLogs();
      fetchEpicenterCultivar();
      checkAndFetchDates();
    }
  }, [effectiveRoomId]);

  const columns = [
    {
      field: "action",
      headerName: "Action",
      minWidth: 120,
      renderCell: (params) => (
        <>
          {editingRowId === params.row.id ? (
            <IconButton
              onClick={() => {
                setEditingRowId(null);
                setEditedData({});
              }}
              size="small"
              color="error"
            >
              <RxCross1 />
            </IconButton>
          ) : (
            <>
              <IconButton
                onClick={(event) => handleMenuClick(event, params.row.id)}
                size="small"
              >
                <BsThreeDotsVertical />
              </IconButton>
              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl) && selectedRowId === params.row.id}
                onClose={handleMenuClose}
              >
                <MenuItem
                  onClick={() => handleEditClick(params.row)}
                  disabled={params.row.id < 0}
                >
                  <MdEdit style={{ marginRight: 8 }} /> Edit
                </MenuItem>
                <MenuItem onClick={() => handleImportClick(params.row)}>
                  <BiImport style={{ marginRight: 8 }} /> Import
                </MenuItem>
              </Menu>
            </>
          )}
        </>
      ),
    },
    {
      field: "growth_phase",
      headerName: "Growth Phase",
      minWidth: 150,
      renderCell: (params) => (
        <div>{params.row?.growth_phase ? params.row?.growth_phase : "-"}</div>
      ),
    },
    {
      field: "day_temp_target",
      headerName: "Day Temp Target",
      minWidth: 160,
      renderCell: (params) =>
        editingRowId === params.row.id ? (
          <TextField
            type="number"
            value={editedData.day_temp_target || ""}
            onChange={(e) =>
              handleFieldChange("day_temp_target", e.target.value)
            }
            size="small"
            fullWidth
          />
        ) : (
          params.value
        ),
    },
    {
      field: "avg_day_temp",
      headerName: "Average Day Temp",
      minWidth: 170,
      renderCell: (params) =>
        editingRowId === params.row.id ? (
          <TextField
            type="number"
            value={editedData.avg_day_temp || ""}
            onChange={(e) => handleFieldChange("avg_day_temp", e.target.value)}
            size="small"
            fullWidth
          />
        ) : (
          <div
            style={{
              backgroundColor: getColorStyle(params.row.avg_day_temp_color),
              padding: "8px",
              width: "100%",
            }}
          >
            {params.value}
          </div>
        ),
    },
    {
      field: "night_temp_target",
      headerName: "Night Temp Target",
      minWidth: 180,
      renderCell: (params) =>
        editingRowId === params.row.id ? (
          <TextField
            type="number"
            value={editedData.night_temp_target || ""}
            onChange={(e) =>
              handleFieldChange("night_temp_target", e.target.value)
            }
            size="small"
            fullWidth
          />
        ) : (
          params.value
        ),
    },
    {
      field: "avg_night_temp",
      headerName: "Average Night Temp",
      minWidth: 190,
      renderCell: (params) =>
        editingRowId === params.row.id ? (
          <TextField
            type="number"
            value={editedData.avg_night_temp || ""}
            onChange={(e) =>
              handleFieldChange("avg_night_temp", e.target.value)
            }
            size="small"
            fullWidth
          />
        ) : (
          <div
            style={{
              backgroundColor: getColorStyle(params.row.avg_night_temp_color),
              padding: "8px",
              width: "100%",
            }}
          >
            {params.value}
          </div>
        ),
    },

    {
      field: "day_humidity_target",
      headerName: "Day Humidity Target",
      minWidth: 190,
      renderCell: (params) =>
        editingRowId === params.row.id ? (
          <TextField
            type="number"
            value={editedData.day_humidity_target || ""}
            onChange={(e) =>
              handleFieldChange("day_humidity_target", e.target.value)
            }
            size="small"
            fullWidth
          />
        ) : (
          params.value
        ),
    },
    {
      field: "avg_day_humidity",
      headerName: "Average Day Humidity",
      minWidth: 210,
      renderCell: (params) =>
        editingRowId === params.row.id ? (
          <TextField
            type="number"
            value={editedData.avg_day_humidity || ""}
            onChange={(e) =>
              handleFieldChange("avg_day_humidity", e.target.value)
            }
            size="small"
            fullWidth
          />
        ) : (
          <div
            style={{
              backgroundColor: getColorStyle(params.row.avg_day_humidity_color),
              padding: "8px",
              width: "100%",
            }}
          >
            {params.value}
          </div>
        ),
    },
    {
      field: "night_humidity_target",
      headerName: "Night Humidity Target",
      minWidth: 210,
      renderCell: (params) =>
        editingRowId === params.row.id ? (
          <TextField
            type="number"
            value={editedData.night_humidity_target || ""}
            onChange={(e) =>
              handleFieldChange("night_humidity_target", e.target.value)
            }
            size="small"
            fullWidth
          />
        ) : (
          params.value
        ),
    },
    {
      field: "avg_night_humidity",
      headerName: "Average Night Humidity",
      minWidth: 230,
      renderCell: (params) =>
        editingRowId === params.row.id ? (
          <TextField
            type="number"
            value={editedData.avg_night_humidity || ""}
            onChange={(e) =>
              handleFieldChange("avg_night_humidity", e.target.value)
            }
            size="small"
            fullWidth
          />
        ) : (
          <div
            style={{
              backgroundColor: getColorStyle(
                params.row.avg_night_humidity_color,
              ),
              padding: "8px",
              width: "100%",
            }}
          >
            {params.value}
          </div>
        ),
    },

    {
      field: "ph_target",
      headerName: "PH Target",
      minWidth: 120,
      renderCell: (params) =>
        editingRowId === params.row.id ? (
          <TextField
            type="number"
            value={editedData.ph_target || ""}
            onChange={(e) => handleFieldChange("ph_target", e.target.value)}
            size="small"
            fullWidth
          />
        ) : (
          params.value
        ),
    },
    {
      field: "epi01_ph_avg",
      headerName: "Epi 01 pH Avg",
      minWidth: 150,
      renderCell: (params) =>
        editingRowId === params.row.id ? (
          <TextField
            type="number"
            value={editedData.epi01_ph_avg || ""}
            onChange={(e) => handleFieldChange("epi01_ph_avg", e.target.value)}
            size="small"
            fullWidth
          />
        ) : (
          <div
            style={{
              backgroundColor: getColorStyle(params.row.epi01_ph_avg_color),
              padding: "8px",
              width: "100%",
            }}
          >
            {params.value}
          </div>
        ),
    },
    {
      field: "epi02_ph_avg",
      headerName: "Epi 02 pH Avg",
      minWidth: 150,
      renderCell: (params) =>
        editingRowId === params.row.id ? (
          <TextField
            type="number"
            value={editedData.epi02_ph_avg || ""}
            onChange={(e) => handleFieldChange("epi02_ph_avg", e.target.value)}
            size="small"
            fullWidth
          />
        ) : (
          <div
            style={{
              backgroundColor: getColorStyle(params.row.epi02_ph_avg_color),
              padding: "8px",
              width: "100%",
            }}
          >
            {params.value}
          </div>
        ),
    },
    {
      field: "epi03_ph_avg",
      headerName: "Epi 03 pH Avg",
      minWidth: 150,
      renderCell: (params) =>
        editingRowId === params.row.id ? (
          <TextField
            type="number"
            value={editedData.epi03_ph_avg || ""}
            onChange={(e) => handleFieldChange("epi03_ph_avg", e.target.value)}
            size="small"
            fullWidth
          />
        ) : (
          <div
            style={{
              backgroundColor: getColorStyle(params.row.epi03_ph_avg_color),
              padding: "8px",
              width: "100%",
            }}
          >
            {params.value}
          </div>
        ),
    },

    {
      field: "tds_target",
      headerName: "TDS Target",
      minWidth: 130,
      renderCell: (params) =>
        editingRowId === params.row.id ? (
          <TextField
            type="number"
            value={editedData.tds_target || ""}
            onChange={(e) => handleFieldChange("tds_target", e.target.value)}
            size="small"
            fullWidth
          />
        ) : (
          params.value
        ),
    },
    {
      field: "epi01_tds_avg",
      headerName: "Epi 01 TDS Avg",
      minWidth: 160,
      renderCell: (params) =>
        editingRowId === params.row.id ? (
          <TextField
            type="number"
            value={editedData.epi01_tds_avg || ""}
            onChange={(e) => handleFieldChange("epi01_tds_avg", e.target.value)}
            size="small"
            fullWidth
          />
        ) : (
          <div
            style={{
              backgroundColor: getColorStyle(params.row.epi01_tds_avg_color),
              padding: "8px",
              width: "100%",
            }}
          >
            {params.value}
          </div>
        ),
    },
    {
      field: "epi02_tds_avg",
      headerName: "Epi 02 TDS Avg",
      minWidth: 160,
      renderCell: (params) =>
        editingRowId === params.row.id ? (
          <TextField
            type="number"
            value={editedData.epi02_tds_avg || ""}
            onChange={(e) => handleFieldChange("epi02_tds_avg", e.target.value)}
            size="small"
            fullWidth
          />
        ) : (
          <div
            style={{
              backgroundColor: getColorStyle(params.row.epi02_tds_avg_color),
              padding: "8px",
              width: "100%",
            }}
          >
            {params.value}
          </div>
        ),
    },
    {
      field: "epi03_tds_avg",
      headerName: "Epi 03 TDS Avg",
      minWidth: 160,
      renderCell: (params) =>
        editingRowId === params.row.id ? (
          <TextField
            type="number"
            value={editedData.epi03_tds_avg || ""}
            onChange={(e) => handleFieldChange("epi03_tds_avg", e.target.value)}
            size="small"
            fullWidth
          />
        ) : (
          <div
            style={{
              backgroundColor: getColorStyle(params.row.epi03_tds_avg_color),
              padding: "8px",
              width: "100%",
            }}
          >
            {params.value}
          </div>
        ),
    },

    {
      field: "water_temp_target",
      headerName: "Water Temp Target",
      minWidth: 180,
      renderCell: (params) =>
        editingRowId === params.row.id ? (
          <TextField
            type="number"
            value={editedData.water_temp_target || ""}
            onChange={(e) =>
              handleFieldChange("water_temp_target", e.target.value)
            }
            size="small"
            fullWidth
          />
        ) : (
          params.value
        ),
    },
    {
      field: "epi01_water_temp_avg",
      headerName: "Epi 01 Water Temp Avg",
      minWidth: 220,
      renderCell: (params) =>
        editingRowId === params.row.id ? (
          <TextField
            type="number"
            value={editedData.epi01_water_temp_avg || ""}
            onChange={(e) =>
              handleFieldChange("epi01_water_temp_avg", e.target.value)
            }
            size="small"
            fullWidth
          />
        ) : (
          <div
            style={{
              backgroundColor: getColorStyle(
                params.row.epi01_water_temp_avg_color,
              ),
              padding: "8px",
              width: "100%",
            }}
          >
            {params.value}
          </div>
        ),
    },
    {
      field: "epi02_water_temp_avg",
      headerName: "Epi 02 Water Temp Avg",
      minWidth: 220,
      renderCell: (params) =>
        editingRowId === params.row.id ? (
          <TextField
            type="number"
            value={editedData.epi02_water_temp_avg || ""}
            onChange={(e) =>
              handleFieldChange("epi02_water_temp_avg", e.target.value)
            }
            size="small"
            fullWidth
          />
        ) : (
          <div
            style={{
              backgroundColor: getColorStyle(
                params.row.epi02_water_temp_avg_color,
              ),
              padding: "8px",
              width: "100%",
            }}
          >
            {params.value}
          </div>
        ),
    },
    {
      field: "epi03_water_temp_avg",
      headerName: "Epi 03 Water Temp Avg",
      minWidth: 220,
      renderCell: (params) =>
        editingRowId === params.row.id ? (
          <TextField
            type="number"
            value={editedData.epi03_water_temp_avg || ""}
            onChange={(e) =>
              handleFieldChange("epi03_water_temp_avg", e.target.value)
            }
            size="small"
            fullWidth
          />
        ) : (
          <div
            style={{
              backgroundColor: getColorStyle(
                params.row.epi03_water_temp_avg_color,
              ),
              padding: "8px",
              width: "100%",
            }}
          >
            {params.value}
          </div>
        ),
    },

    {
      field: "avg_light_level",
      headerName: "Average Light Level",
      minWidth: 190,
      renderCell: (params) =>
        editingRowId === params.row.id ? (
          <TextField
            type="number"
            value={editedData.avg_light_level || ""}
            onChange={(e) =>
              handleFieldChange("avg_light_level", e.target.value)
            }
            size="small"
            fullWidth
          />
        ) : (
          params.value
        ),
    },
    {
      field: "co2_target",
      headerName: "CO2 Target",
      minWidth: 130,
      renderCell: (params) =>
        editingRowId === params.row.id ? (
          <TextField
            type="number"
            value={editedData.co2_target || ""}
            onChange={(e) => handleFieldChange("co2_target", e.target.value)}
            size="small"
            fullWidth
          />
        ) : (
          params.value
        ),
    },
    {
      field: "avg_co2",
      headerName: "Average CO2",
      minWidth: 150,
      renderCell: (params) =>
        editingRowId === params.row.id ? (
          <TextField
            type="number"
            value={editedData.avg_co2 || ""}
            onChange={(e) => handleFieldChange("avg_co2", e.target.value)}
            size="small"
            fullWidth
          />
        ) : (
          <div
            style={{
              backgroundColor: getColorStyle(params.row.avg_co2_color),
              padding: "8px",
              width: "100%",
            }}
          >
            {params.value}
          </div>
        ),
    },
    { field: "last_updated", headerName: "Date Last Updated", minWidth: 180 },
  ];

  const epicenterColumns = [
    {
      field: "planting_location",
      headerName: "Planting Location",
      width: 200,
    },
    {
      field: "cultivar",
      headerName: "Cultivar",
      flex: 1,
    },
  ];

  return (
    <>
      {/* Epicenter Table Section */}
      <div style={{ marginBottom: 24 }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            flexWrap: "nowrap",
            gap: 16,
            marginBottom: 12,
          }}
        >
          <div className="form_field_wrapper d-flex align-items-center justify-content-center m-0">
            <label>Planted : </label>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                format="MM-DD-YYYY"
                className="ms-2"
                value={planted ? dayjs(planted) : null}
                onChange={(date) => handleDateChange("planted", date)}
                slotProps={{ textField: { size: "small" } }}
                readOnly
              />
            </LocalizationProvider>
          </div>
          <div className="form_field_wrapper d-flex align-items-center justify-content-center m-0">
            <label>Harvested : </label>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                format="MM-DD-YYYY"
                className="ms-2"
                value={harvest ? dayjs(harvest) : null}
                onChange={(date) => handleDateChange("harvest", date)}
                slotProps={{ textField: { size: "small" } }}
              />
            </LocalizationProvider>
          </div>
        </div>
        {/* epicenter table */}
        <DataGrid
          rows={epicenterRows}
          columns={epicenterColumns}
          loading={epicenterLoading}
          autoHeight
          hideFooter
          disableRowSelectionOnClick
          showCellVerticalBorder
          showColumnVerticalBorder
          sx={{
            border: "1px solid #E0E0E0",
            backgroundColor: "#fff",
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: "#EEF0FF",
              fontWeight: 600,
            },
          }}
        />
      </div>

      {/* Main Room Logs Table Section */}
      <div
        className="table-filter-wrap d-flex justify-content-between pb-0"
        style={{ marginBottom: 12 }}
      >
        <div className="header_search-wrap">
          {/* <div className="header_search_bar common_content d-flex">
            <div className="input-group input_group">
              <button className="btn search-btn search_btn">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="15"
                  height="15"
                  viewBox="0 0 21 21"
                  fill="none"
                >
                  <circle
                    cx="9.98856"
                    cy="9.98856"
                    r="8.98856"
                    stroke="#495057"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M16.2402 16.7071L19.7643 20.222"
                    stroke="#495057"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
              <input
                type="search"
                className="form-control form_control"
                placeholder="Search..."
                aria-label="Search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div> */}
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          <Tooltip
            title={
              !planted
                ? "There is no plant in the room"
                : !hasNonDefault
                  ? "No non-default records found"
                  : ""
            }
            arrow
            placement="top"
          >
            <span>
              <Button
                variant="outlined"
                className="btn btn-border"
                style={{ textTransform: "none" }}
                onClick={() => setShowArchiveDialog(true)}
                disabled={!planted || !hasNonDefault}
              >
                <MdArchive style={{ marginRight: 4 }} /> Archive
              </Button>
            </span>
          </Tooltip>
          {/* <Button
            variant="outlined"
            className="filters-btn"
            style={{ textTransform: "none" }}
          >
            <TbFilterEdit /> Filters
          </Button> */}
        </div>
      </div>
      <div
        className="frozen-table-wrapper"
        style={{
          height: `${rows?.length > 0 ? "" : "200px"}`,
          width: "100%",
        }}
      >
        {/* Fake frozen header */}
        <div className="fake-frozen-header">
          <div className="fake-header-cell" style={{ width: 120 }}>
            Action
          </div>
          <div className="fake-header-cell" style={{ width: 150 }}>
            Growth Phase
          </div>
        </div>
        {/* Main table */}
        <DataGrid
          rows={rows.filter(
            (row) =>
              !search ||
              row.growth_phase?.toLowerCase().includes(search.toLowerCase()),
          )}
          columns={columns}
          loading={loading || externalLoading}
          getRowHeight={() => "auto"}
          pinnedColumns={{
            left: ["action", "growth_phase"],
          }}
          sx={{
            border: "1px solid #E0E0E0",
            [`& .${gridClasses.cell}`]: { py: 0.5 },
            // '& .MuiDataGrid-cell[data-field="action"]': {
            //   position: "sticky !important",
            //   left: "0 !important",
            //   backgroundColor: "white !important",
            //   zIndex: 3,
            // },
            // '& .MuiDataGrid-cell[data-field="growth_phase"]': {
            //   position: "sticky !important",
            //   left: "120px !important",
            //   backgroundColor: "white !important",
            //   zIndex: 3,
            // },
          }}
          autoHeight
          showCellVerticalBorder
          showColumnVerticalBorder
          hideFooter
        />
      </div>

      {/* Notes Table Section */}
      <div style={{ marginTop: 24 }}>
        <DataGrid
          rows={rows}
          columns={[
            {
              field: "action",
              headerName: "Action",
              minWidth: 120,
              renderCell: (params) => (
                <>
                  {editingNoteRowId === params.row.id ? (
                    <IconButton
                      onClick={() => {
                        setEditingNoteRowId(null);
                        setEditedNote("");
                      }}
                      size="small"
                    >
                      <RxCross1 />
                    </IconButton>
                  ) : (
                    <>
                      <IconButton
                        onClick={(event) => {
                          setNoteAnchorEl(event.currentTarget);
                          setSelectedNoteRowId(params.row.id);
                        }}
                        size="small"
                      >
                        <BsThreeDotsVertical />
                      </IconButton>
                      <Menu
                        anchorEl={noteAnchorEl}
                        open={
                          Boolean(noteAnchorEl) &&
                          selectedNoteRowId === params.row.id
                        }
                        onClose={() => setNoteAnchorEl(null)}
                      >
                        <MenuItem
                          onClick={() => {
                            setEditingNoteRowId(params.row.id);
                            setEditedNote(params.row.note || "");
                            setNoteAnchorEl(null);
                          }}
                          // disabled={params.row.id < 0}
                        >
                          <MdEdit style={{ marginRight: 8 }} /> Edit
                        </MenuItem>
                      </Menu>
                    </>
                  )}
                </>
              ),
            },
            {
              field: "growth_phase",
              headerName: "Growth Phase",
              minWidth: 150,
            },
            {
              field: "note",
              headerName: "Note",
              flex: 1,
              minWidth: 300,
              renderCell: (params) =>
                editingNoteRowId === params.row.id ? (
                  <TextField
                    value={editedNote}
                    onChange={(e) => {
                      const value = e.target.value;
                      setEditedNote(value);
                      handleNoteChange(
                        value,
                        params.row.id,
                        params.row.growth_phase_id,
                      );
                    }}
                    size="small"
                    fullWidth
                    multiline
                    autoFocus
                    onKeyDown={(e) => e.stopPropagation()} // prevents DataGrid interference
                  />
                ) : (
                  params.value || "-"
                ),
            },
          ]}
          loading={loading || externalLoading}
          getRowHeight={() => "auto"}
          sx={{
            border: "1px solid #E0E0E0",
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: "#EEF0FF",
              fontWeight: 600,
            },
            [`& .${gridClasses.cell}`]: { py: 0.5 },
          }}
          autoHeight
          showCellVerticalBorder
          showColumnVerticalBorder
          hideFooter
        />
      </div>

      <RoomLogImportModal
        open={showImportModal}
        onClose={() => setShowImportModal(false)}
        onImport={handleImport}
      />

      <Modal
        open={showArchiveDialog}
        onClose={() => setShowArchiveDialog(false)}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <div className="modal_block">
          <div className="modal_header">
            <div className="modal_title_wrapper">
              <h2 className="modal_title">Are you Sure ?</h2>
            </div>
            <div className="cancel-btn-wrap">
              <RxCross1 onClick={() => setShowArchiveDialog(false)} />
            </div>
          </div>
          <div className="modal_content">
            <div className="modal_content_inner">
              <div>
                <TbAlertHexagonFilled />
              </div>
              <div>
                <p>Are you sure you want to archive this room log?</p>
              </div>
            </div>
          </div>
          <div className="modal_footer">
            <button
              onClick={() => setShowArchiveDialog(false)}
              className="btn btn-border"
            >
              Cancel
            </button>
            <button onClick={handleArchive} className="btn btn-primary">
              Archive
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default RoomLogMasterTable;
